export interface CountrySpecificFieldConfig {
  // Tax registration fields
  taxRegistrationLabel: string;
  taxRegistrationDescription: string;
  taxNumberLabel: string;
  taxNumberPlaceholder: string;
  taxNumberDescription: string;
  
  // Turnover/Revenue fields
  revenueLabel: string;
  revenueDescription: string;
  currencySymbol: string;
  
  // Address field labels
  addressLabels: {
    streetNumber: string;
    streetName: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  
  // Address placeholders
  addressPlaceholders: {
    streetNumber: string;
    streetName: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  
  // Registration/business number
  registrationNumberLabel: string;
  registrationNumberPlaceholder: string;
  registrationNumberDescription: string;
  
  // Bank details
  bankDetailsLabel: string;
  bankDetailsPlaceholder: string;
  bankDetailsDescription: string;
}

const COUNTRY_FIELD_CONFIGS: Record<string, CountrySpecificFieldConfig> = {
  'GB': {
    // Tax registration (VAT in UK)
    taxRegistrationLabel: "VAT Registered",
    taxRegistrationDescription: "Check this if your business is registered for VAT with HMRC",
    taxNumberLabel: "VAT Number",
    taxNumberPlaceholder: "Enter VAT number (e.g., GB123456789)",
    taxNumberDescription: "Enter your 9-digit VAT registration number starting with 'GB'",
    
    // Revenue fields
    revenueLabel: "Yearly Turnover (£)",
    revenueDescription: "Enter your estimated yearly turnover over a 12-month rolling period",
    currencySymbol: "£",
    
    // Address labels
    addressLabels: {
      streetNumber: "Street Number",
      streetName: "Street Name",
      city: "Town/City",
      state: "County",
      postalCode: "Postcode",
      country: "Country"
    },
    
    // Address placeholders
    addressPlaceholders: {
      streetNumber: "123",
      streetName: "High Street",
      city: "London",
      state: "Greater London",
      postalCode: "SW1A 1AA",
      country: "United Kingdom"
    },
    
    // Registration number
    registrationNumberLabel: "Registration Number",
    registrationNumberPlaceholder: "Enter registration number",
    registrationNumberDescription: "Enter your business registration number (e.g., Company House number for Limited Companies)",
    
    // Bank details
    bankDetailsLabel: "Bank Details",
    bankDetailsPlaceholder: "Enter bank details (account numbers, sort codes, etc.)",
    bankDetailsDescription: "Enter your bank account details, sort codes, or any other banking information for invoices"
  },
  
  'US': {
    // Tax registration (W-9 acknowledgment in US)
    taxRegistrationLabel: "W-9 Form Acknowledged",
    taxRegistrationDescription: "Check this if you have completed and acknowledged the W-9 form for tax reporting purposes",
    taxNumberLabel: "Tax ID (EIN/SSN)",
    taxNumberPlaceholder: "XX-XXXXXXX or XXX-XX-XXXX",
    taxNumberDescription: "Enter your Employer Identification Number (EIN: XX-XXXXXXX) or Social Security Number (SSN: XXX-XX-XXXX) as provided on your W-9 form",
    
    // Revenue fields
    revenueLabel: "Annual Revenue ($)",
    revenueDescription: "Enter your estimated annual revenue over a 12-month period",
    currencySymbol: "$",
    
    // Address labels
    addressLabels: {
      streetNumber: "Street Number",
      streetName: "Street Name",
      city: "City",
      state: "State",
      postalCode: "ZIP Code",
      country: "Country"
    },
    
    // Address placeholders
    addressPlaceholders: {
      streetNumber: "123",
      streetName: "Main Street",
      city: "New York",
      state: "NY",
      postalCode: "10001",
      country: "United States"
    },
    
    // Registration number
    registrationNumberLabel: "Business Registration Number",
    registrationNumberPlaceholder: "Enter business registration number",
    registrationNumberDescription: "Enter your business registration number (e.g., Secretary of State filing number, if applicable)",
    
    // Bank details
    bankDetailsLabel: "Bank Details",
    bankDetailsPlaceholder: "Enter bank details (account number, routing number, etc.)",
    bankDetailsDescription: "Enter your bank account details, routing numbers, or any other banking information for invoices"
  }
};

/**
 * Get country-specific field configuration for contractor entity forms
 */
export function getCountryFieldConfig(countryCode: string): CountrySpecificFieldConfig {
  return COUNTRY_FIELD_CONFIGS[countryCode] || COUNTRY_FIELD_CONFIGS['GB']; // Default to GB if country not found
}

/**
 * Check if a country uses VAT registration (UK, EU countries)
 */
export function usesVATRegistration(countryCode: string): boolean {
  return countryCode === 'GB'; // Add other VAT countries as needed
}

/**
 * Check if a country uses W-9 acknowledgment (US)
 */
export function usesW9Acknowledgment(countryCode: string): boolean {
  return countryCode === 'US';
}

/**
 * Check if a country uses sales tax registration (Canada, etc.)
 * Note: US removed as they use W-9 acknowledgment instead
 */
export function usesSalesTaxRegistration(countryCode: string): boolean {
  return false; // Add countries that use sales tax registration as needed (e.g., 'CA' for Canada)
}

/**
 * Get the appropriate tax registration field name for form validation
 */
export function getTaxRegistrationFieldName(countryCode: string): string {
  if (usesVATRegistration(countryCode)) {
    return 'vatRegistered';
  }
  if (usesW9Acknowledgment(countryCode)) {
    return 'w9Acknowledged';
  }
  if (usesSalesTaxRegistration(countryCode)) {
    return 'salesTaxRegistered';
  }
  return 'taxRegistered'; // Generic fallback
}

/**
 * Get the appropriate tax number field name for form validation
 */
export function getTaxNumberFieldName(countryCode: string): string {
  if (usesVATRegistration(countryCode)) {
    return 'vatNumber';
  }
  if (usesW9Acknowledgment(countryCode)) {
    return 'taxId'; // For US, this would be EIN or SSN
  }
  if (usesSalesTaxRegistration(countryCode)) {
    return 'salesTaxNumber';
  }
  return 'taxNumber'; // Generic fallback
}

/**
 * Extract country code from entity data
 */
export function getCountryCodeFromEntity(entity: any): string {
  // Check various possible sources for country code
  if (entity?.registeredAddress?.country_code) {
    return entity.registeredAddress.country_code;
  }
  
  // Handle US addresses
  if (entity?.registeredAddress?.administrative_area_level_1 === 'CA' && 
      entity?.registeredAddress?.country === 'USA') {
    return 'US';
  }
  if (entity?.registeredAddress?.country === 'United States') {
    return 'US';
  }
  
  // Handle UK addresses
  if (entity?.registeredAddress?.country === 'United Kingdom') {
    return 'GB';
  }
  
  // Check for 2-letter state codes that indicate US
  const usStateCodes = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
  if (entity?.registeredAddress?.administrative_area_level_1 && 
      usStateCodes.includes(entity.registeredAddress.administrative_area_level_1)) {
    return 'US';
  }
  
  // Check if it's a US ZIP code pattern (5 digits or 5+4 format)
  const zipPattern = /^\d{5}(-\d{4})?$/;
  if (entity?.registeredAddress?.postal_code && 
      zipPattern.test(entity.registeredAddress.postal_code)) {
    return 'US';
  }
  
  // Default to GB for backwards compatibility
  return 'GB';
} 